import { PolicyFormInstance, usePolicyScope } from '../../../../field-policy';
import { FieldPath } from './fields.base';
import { finalizePolicy } from './resolve';
import { FinalizeContext, FinalizeFormValues } from './types';

// No hand-written form interface: this is the registry's own FormValuesOf<typeof FIELDS_BASE> -
// the same type the real form already carries at runtime (see useFormPolicy.ts). Leaves are
// `unknown`; call sites that need a concrete type cast narrowly at the point of use.
//
// `PolicyFormInstance` is the framework's own name for "whatever useFormPolicy's `form` actually
// is" - this file has no business knowing that's `useFormWithNameInputProp` plus
// `useChangedFieldsTracking` under the hood, so it doesn't import either.
export type FinalizeForm = PolicyFormInstance<FinalizeFormValues>;

interface FieldOptions {
  /** Applied to every field on the tab, e.g. the procedure-header switch being off. */
  disabledWhen?: boolean;
  /**
   * Which tab is asking.
   *
   * Only fields whose rule names a section need this: they render in one tab for some variants and
   * another tab for the rest, so `present` alone cannot say who draws them. A rule with no section
   * renders wherever it is called, which is every field but the delivery-terms trio.
   */
  section?: string;
}

/**
 * The shared form, the resolved policy for the current variant, and the props a field needs.
 *
 * `fieldProps(path)` is spread straight onto <PolicyField>:
 *
 *     <PolicyField {...fieldProps(PAYMENT_METHOD)} span={6} />
 *
 * It returns `rule` already narrowed by the tab's own gates - out of section becomes
 * `present: false`. A tab-level `disabledWhen` is returned as its OWN sibling prop, the same
 * channel a call site's own `disabledWhen={...}` already uses, NOT baked into `rule.disabled`:
 * `<PolicyField>`/`<PolicyArrayField>` deliberately keep a header toggle's own disabled state to
 * `rule.disabled` alone (see their shared doc comment) - a field whose `headerToggle` points at the
 * SAME value this tab-level `disabledWhen` is derived from (e.g. `customsProcedureCurrent`, gated
 * by `goodShipmentGlobal.procedureHeader`, disabled here whenever that SAME header is off) would
 * otherwise make its own switch a one-way control the moment `disabled` folded this in.
 *
 * A call site combining BOTH a hook-level `disabledWhen` and its own field-level one must compose
 * them itself (`disabledWhen={hookDisabledWhen || extra}`) - spreading then re-declaring the same
 * prop always overrides, never merges. No call site does this today.
 *
 * `rule(path)` stays available and is the raw policy answer - the hand-written call sites read
 * `label` / `code` / `dataPw` / `systems` off it.
 */
export const useFinalizeFieldRules = ({ disabledWhen, section }: FieldOptions = {}) => {
  const scope = usePolicyScope<FinalizeContext, FieldPath, FinalizeForm>(finalizePolicy);
  const { form, rule } = scope;

  const fieldProps = (path: FieldPath) => {
    const resolved = rule(path);
    const ownedByThisTab = !resolved.section || resolved.section === section;

    return {
      path,
      form,
      disabledWhen: Boolean(disabledWhen),
      rule: { ...resolved, present: resolved.present && ownedByThisTab },
    };
  };

  return { ...scope, fieldProps };
};
